import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MagnifyingGlassPlus } from "@phosphor-icons/react";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { PortalLightbox } from "@/components/PortalLightbox";
import { CtaBanner, Eyebrow } from "@/components/site/Blocks";
import { BeforeAfter } from "@/components/site/BeforeAfter";
import { ArrowLink, Rule, SplitText } from "@/components/site/Motion";
import { LineTabs } from "@/components/site/Shop";
import { SITE_IMG } from "@/lib/siteImages";
import { siteImg, usePageHead } from "@/lib/siteContent";
import { useReveal } from "@/lib/useReveal";
import { API } from "@/lib/api";

const PAGE = 9;
// Nordic masonry mixes tall + landscape tiles for rhythm
const RATIO = ["aspect-[723/850]", "aspect-[723/599]", "aspect-[723/850]", "aspect-[723/599]", "aspect-[723/850]", "aspect-[723/850]"];

// Serve sharp tiles on big screens: request sized copies off the full image (not the 960px thumb)
const photoUrl = (p, w) => `${API}/public/showroom/photo/${p}${w ? `?w=${w}` : ""}`;
const SRCSET_W = [600, 900, 1200, 1600, 2000];
const srcSetOf = (p) => SRCSET_W.map((w) => `${photoUrl(p, w)} ${w}w`).join(", ");
const TILE_SIZES = "(min-width:1024px) 33vw, (min-width:640px) 50vw, 100vw";

const excerptOf = (i) =>
  i.description ||
  `${i.title || i.color} — fabricated, templated and installed by our own crew${i.product ? ` in ${i.product}` : i.color ? ` in ${i.color}` : ""}${i.material ? ` ${i.material.toLowerCase()}` : ""}.`;

const FilterBar = ({ cats, cat, onPick }) => (
  <LineTabs
    testIdPrefix="gallery-filter"
    value={cat}
    onChange={onPick}
    items={cats.map((c) => ({ id: c, label: c === "All" ? "All projects" : c, testId: `gallery-filter-${c.toLowerCase()}` }))}
  />
);

const WorkTile = ({ i, n, onOpen }) => {
  const meta = [i.category, i.product ? `Made with ${i.product}` : i.material].filter(Boolean);
  return (
    <article className="s-pf group mb-4 break-inside-avoid" data-testid={`gallery-item-${i.id}`}>
      <div className={`s-pf-fig relative ${RATIO[n % RATIO.length]}`}>
        {i.before ? (
          <BeforeAfter
            before={photoUrl(i.before, 1600)}
            after={photoUrl(i.path, 1600)}
            alt={i.title || i.color}
            className="absolute inset-0"
            testId={`gallery-compare-${i.id}`}
            onOpen={onOpen}
          />
        ) : (
          <Link to={`/gallery/${i.id}`} data-testid={`gallery-open-${i.id}`} className="absolute inset-0 block h-full w-full text-left">
            <img
              src={photoUrl(i.path, 1200)}
              srcSet={srcSetOf(i.path)}
              sizes={TILE_SIZES}
              alt={i.title || i.color}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </Link>
        )}
        <Link to={`/gallery/${i.id}`} className="s-pf-details" data-testid={`gallery-details-${i.id}`} aria-label={`${i.title || i.color} — see the project`}>
          Details <ArrowUpRight size={14} weight="bold" />
        </Link>
        {i.category && (
          <span className="absolute bottom-4 left-4 z-[2]">
            <span className="s-badge s-badge-light">{i.category}</span>
          </span>
        )}
        <button
          type="button"
          onClick={onOpen}
          data-testid={`gallery-zoom-${i.id}`}
          aria-label="Zoom photo"
          className="absolute right-3 top-3 z-[2] flex h-9 w-9 items-center justify-center bg-white/94 text-[color:var(--s-ink)] opacity-0 transition-opacity duration-300 hover:bg-[color:var(--s-ink)] hover:text-white focus-visible:opacity-100 group-hover:opacity-100"
        >
          <MagnifyingGlassPlus size={14} weight="bold" />
        </button>
      </div>
      <div className="mt-4">
        {meta.length > 0 && (
          <div className="s-micro !text-[10px] text-[color:var(--s-mute)]">
            {meta.map((m, k) => (
              <span key={m} data-testid={k === 1 && i.product ? `gallery-product-${i.id}` : undefined}>
                {k > 0 && ", "}
                {m}
              </span>
            ))}
          </div>
        )}
        <h3 className="mt-2 text-lg font-medium tracking-tight text-[color:var(--s-ink)] transition-colors duration-300 group-hover:text-[color:var(--s-gold)]">
          <Link to={`/gallery/${i.id}`} data-testid={`gallery-title-${i.id}`}>{i.title || i.color}</Link>
        </h3>
        <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-[color:var(--s-mute)]">{excerptOf(i)}</p>
      </div>
    </article>
  );
};

export default function SiteGallery() {
  const site = useSite();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [shot, setShot] = useState(-1);
  const [limit, setLimit] = useState(PAGE);
  const cfg = site?.config || {};
  usePageHead({
    title: "Project gallery",
    description:
      "Finished kitchens, bathrooms and features in granite, quartz and natural stone, fabricated and installed by Brioni Granite in St. Louis.",
    suffix: site?.shop?.name,
  });

  useEffect(() => {
    fetch(`${API}/public/gallery`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]));
  }, []);
  useEffect(() => setLimit(PAGE), [cat]);
  useReveal([site, items, cat, limit]);

  const cats = ["All", ...Array.from(new Set(items.map((i) => i.category).filter(Boolean)))];
  const shown = cat === "All" ? items : items.filter((i) => i.category === cat);
  const visible = shown.slice(0, limit);
  const shots = shown.map((i) => ({
    path: i.path,
    thumb: i.thumb,
    title: i.title || i.color,
    sub: [i.product ? `Made with ${i.product}` : i.color, i.category].filter(Boolean).join(" · "),
  }));

  return (
    <SiteShell site={site} testId="site-gallery">
      <section className="bg-[color:var(--s-bg)] pb-8 pt-8 sm:pt-12" data-testid="gallery-hero">
        <div className="s-container">
          <Rule className="is-in" />
          <div className="py-14 sm:py-20">
            <Eyebrow>{cfg.gallery?.micro || "Portfolio"}</Eyebrow>
            <h1 className="s-hx s-spaced mt-6 break-words text-5xl text-[color:var(--s-ink)] sm:text-7xl lg:text-8xl" data-testid="gallery-heading">
              <SplitText text={cfg.gallery?.page_heading || "Our work."} />
            </h1>
            {cfg.gallery?.sub && (
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-[color:var(--s-mute)] sm:text-lg" data-testid="gallery-sub">
                {cfg.gallery.sub}
              </p>
            )}
          </div>
          {items.length > 0 && cats.length > 1 && <FilterBar cats={cats} cat={cat} onPick={setCat} />}
        </div>
      </section>

      <section className="bg-[color:var(--s-bg)] pb-24 pt-10 sm:pb-32">
        <div className="s-container">
          {items.length === 0 ? (
            <div className="py-20 text-center" data-testid="gallery-empty">
              <p className="mx-auto max-w-md text-lg font-light leading-relaxed text-[color:var(--s-mute)]">
                We are putting our latest installs together. In the meantime, walk the racks with
                us or ask for photos of a stone you like.
              </p>
              <div className="mt-10 flex justify-center">
                <ArrowLink to="/contact" testId="gallery-contact">Ask for photos</ArrowLink>
              </div>
            </div>
          ) : (
            <div className="columns-1 gap-4 sm:columns-2 lg:columns-3" data-testid="gallery-grid">
              {visible.map((i, n) => (
                <WorkTile key={i.id} i={i} n={n} onOpen={() => setShot(n)} />
              ))}
            </div>
          )}

          {visible.length < shown.length && (
            <div className="mt-14 flex justify-center">
              <button type="button" className="s-ghost" data-testid="gallery-load-more" onClick={() => setLimit((n) => n + PAGE)}>
                Load more projects
              </button>
            </div>
          )}

          {items.length > 0 && (
            <div className="mt-20 border-t border-[color:var(--s-line)] pt-10">
              <ArrowLink to="/contact" testId="gallery-page-quote">Start a project like this</ArrowLink>
            </div>
          )}
        </div>
      </section>

      <CtaBanner cfg={cfg.cta} shop={site?.shop || {}} image={siteImg(cfg.cta?.image || cfg.why?.image, SITE_IMG.kitchenDark)} testId="gallery-cta" />

      {shot >= 0 && shots.length > 0 && (
        <PortalLightbox
          prefix="site-lightbox"
          shots={shots}
          index={shot}
          srcOf={(path) => `${API}/public/showroom/photo/${path}`}
          onClose={() => setShot(-1)}
        />
      )}
    </SiteShell>
  );
}
